import { renderAccount } from './account';
import { fetchMe, getSession, login, setSession, type AuthUser } from './api';
import { renderBoard } from './board';
import { aarDetail, renderAar } from './aar';
import { laneDetail, renderLane } from './lane';
import { queueCount, startQueueLoop, flushQueue } from './offline';
import { renderLibrary } from './library';
import { renderPerson, renderRoster } from './roster';
import { renderSpot, spotDetail } from './spot';
import { el, go, onNavigate, textInput, toast } from './ui';

let user: AuthUser | null = null;
let dispose: (() => void) | null = null;

function $(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error('Missing #' + id);
  return node;
}

type Route =
  | { name: 'spot' }
  | { name: 'spot-edit'; id: string }
  | { name: 'lane' }
  | { name: 'lane-new' }
  | { name: 'lane-edit'; id: string }
  | { name: 'aar' }
  | { name: 'aar-edit'; id: string }
  | { name: 'board' }
  | { name: 'spot-detail'; id: string }
  | { name: 'lane-detail'; id: string }
  | { name: 'aar-detail'; id: string }
  | { name: 'roster' }
  | { name: 'person'; id: string }
  | { name: 'library' }
  | { name: 'account' };

function parseRoute(): Route {
  const parts = (location.hash || '#spot').replace(/^#/, '').split('/').filter(Boolean);
  const head = parts[0] || 'spot';
  if (head === 'board' && parts[1] === 'spot' && parts[2]) {
    return { name: 'spot-detail', id: decodeURIComponent(parts[2]) };
  }
  if (head === 'board' && parts[1] === 'lane' && parts[2]) {
    return { name: 'lane-detail', id: decodeURIComponent(parts[2]) };
  }
  if (head === 'board' && parts[1] === 'aar' && parts[2]) {
    return { name: 'aar-detail', id: decodeURIComponent(parts[2]) };
  }
  if (head === 'spot' && parts[1]) return { name: 'spot-edit', id: decodeURIComponent(parts[1]) };
  if (head === 'lane' && parts[1] === 'new') return { name: 'lane-new' };
  if (head === 'lane' && parts[1]) return { name: 'lane-edit', id: decodeURIComponent(parts[1]) };
  if (head === 'aar' && parts[1]) return { name: 'aar-edit', id: decodeURIComponent(parts[1]) };
  if (head === 'aar') return { name: 'aar' };
  if (head === 'roster' && parts[1]) return { name: 'person', id: decodeURIComponent(parts[1]) };
  if (head === 'lane') return { name: 'lane' };
  if (head === 'board') return { name: 'board' };
  if (head === 'roster') return { name: 'roster' };
  if (head === 'library') return { name: 'library' };
  if (head === 'account') return { name: 'account' };
  return { name: 'spot' };
}

function paintTabs(route: Route): void {
  const head =
    route.name === 'spot' || route.name === 'spot-edit' || route.name === 'spot-detail'
      ? 'spot'
      : route.name === 'lane' || route.name === 'lane-new' || route.name === 'lane-edit' || route.name === 'lane-detail'
        ? 'lane'
        : route.name === 'aar' || route.name === 'aar-edit' || route.name === 'aar-detail'
          ? 'aar'
          : route.name === 'board'
          ? 'board'
          : route.name === 'roster' || route.name === 'person'
            ? 'roster'
            : route.name === 'library'
              ? 'library'
              : '';
  for (const btn of document.querySelectorAll<HTMLButtonElement>('#tabbar button')) {
    const on = btn.dataset.tab === head;
    if (on) btn.setAttribute('aria-current', 'page');
    else btn.removeAttribute('aria-current');
  }
}

async function paintSync(): Promise<void> {
  const btn = $('sync-btn');
  const count = await queueCount().catch(() => 0);
  if (!count || !user) {
    btn.hidden = true;
    return;
  }
  btn.hidden = false;
  btn.textContent = count === 1 ? '1 waiting' : `${count} waiting`;
}

function renderLogin(root: HTMLElement): void {
  const username = textInput({ placeholder: 'Username', required: true });
  username.autocomplete = 'username';
  const password = textInput({ type: 'password', placeholder: 'Password', required: true });
  password.autocomplete = 'current-password';
  const error = el('p', { className: 'form-error' });
  const submit = el('button', {
    type: 'submit',
    className: 'btn btn-primary',
    textContent: 'Sign in',
  });
  const cover = el('div', { className: 'login-cover' }, [
    el('img', { className: 'login-art', src: './cover.jpg', alt: '' }),
  ]);
  const form = el('form', { className: 'login' }, [
    cover,
    el('h1', { textContent: 'MDRC' }),
    el('p', { className: 'muted', textContent: 'Critique and evaluation' }),
    el('label', { className: 'field' }, [
      el('span', { className: 'field-label', textContent: 'Username' }),
      username,
    ]),
    el('label', { className: 'field' }, [
      el('span', { className: 'field-label', textContent: 'Password' }),
      password,
    ]),
    error,
    submit,
  ]);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.textContent = '';
      submit.disabled = true;
      try {
        user = await login(username.value.trim(), password.value);
        go('spot');
      } catch (err) {
        error.textContent = (err as Error).message;
        submit.disabled = false;
      }
    })();
  });
  root.replaceChildren(form);
}

async function show(): Promise<void> {
  dispose?.();
  dispose = null;
  const route = parseRoute();
  const main = $('main');
  const tabs = $('tabbar');
  const account = $('account-btn');
  if (!user) {
    tabs.hidden = true;
    account.hidden = true;
    $('sync-btn').hidden = true;
    renderLogin(main);
    return;
  }
  tabs.hidden = false;
  account.hidden = false;
  paintTabs(route);
  void paintSync();
  main.replaceChildren();
  if (route.name === 'account') dispose = renderAccount(main, user);
  else if (route.name === 'board') dispose = renderBoard(main, user);
  else if (route.name === 'roster') dispose = renderRoster(main);
  else if (route.name === 'library') dispose = renderLibrary(main);
  else if (route.name === 'person') dispose = renderPerson(main, route.id);
  else if (route.name === 'spot-detail') dispose = spotDetail(main, { user, id: route.id });
  else if (route.name === 'lane-detail') dispose = laneDetail(main, { user, id: route.id });
  else if (route.name === 'aar-detail') dispose = aarDetail(main, { user, id: route.id });
  else if (route.name === 'aar' || route.name === 'aar-edit') {
    dispose = renderAar(main, { user, aarId: route.name === 'aar-edit' ? route.id : undefined });
  } else if (route.name === 'lane' || route.name === 'lane-new' || route.name === 'lane-edit') {
    dispose = renderLane(main, {
      user,
      fresh: route.name === 'lane' || route.name === 'lane-new',
      laneId: route.name === 'lane-edit' ? route.id : undefined,
    });
  } else if (route.name === 'spot-edit') dispose = renderSpot(main, { user, spotId: route.id });
  else dispose = renderSpot(main, { user });
}

function bindChrome(): void {
  for (const btn of document.querySelectorAll<HTMLButtonElement>('#tabbar button')) {
    btn.addEventListener('click', () => go(btn.dataset.tab || 'spot'));
  }
  $('account-btn').addEventListener('click', () => go('account'));
  $('sync-btn').addEventListener('click', () => {
    void (async () => {
      const result = await flushQueue();
      await paintSync();
      if (result.left === 0) toast('All reports sent', 'ok');
      else toast(`${result.left} still waiting`, 'info');
    })();
  });
  window.addEventListener('mdrc-queue', () => void paintSync());
  window.addEventListener('mdrc-unauthorized', () => {
    user = null;
    setSession(null);
    void show();
  });
}

async function boot(): Promise<void> {
  bindChrome();
  onNavigate(() => void show());
  window.addEventListener('hashchange', () => void show());
  startQueueLoop();
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('./sw.js').catch(() => undefined);
  }
  if (getSession()) {
    try {
      user = await fetchMe();
    } catch {
      user = null;
      setSession(null);
    }
  }
  if (!location.hash) location.hash = '#spot';
  await show();
  void flushQueue().then(() => paintSync());
}

void boot();
